import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import styled, { withTheme } from 'styled-components';
import Color from 'color';
import { defaultTheme } from '../../constants/appConfig';
import newid from '../../utils/newid';
import useCombinedRefs from '../hooks/useCombinedRefs';
import AnimatedErrorMessage from './AnimatedErrorMessage';
import { FormContext } from './Form';
import Svg from './Svg';

function getRandomInt() {
  return newid() + Date.now() + (Math.random() * 1000).toFixed();
}

const StyledUiInput = styled.div`
  .input-wrap {
    &:hover {
      &:before {
        background-color: ${Color('#d6d5d6').darken(0.2).hex()};
      }
    }
  }
  &.focused {
    .input-wrap {
      &:before {
        background-color: ${props => (props.theme ? props.theme.primaryBgColor : defaultTheme.primaryBgColor)};
      }
    }
  }
  &.focused,
  &.filled {
    .stroke-colored-el {
      stroke: ${props => (props.theme ? props.theme.primaryBgColor : defaultTheme.primaryBgColor)};
    }
    .fill-colored-el {
      fill: ${props => (props.theme ? props.theme.primaryBgColor : defaultTheme.primaryBgColor)};
    }
  }

  &.invalid {
    .input-wrap {
      &:before {
         background-color: #ff5252;
      }
    }
  }
`;

class Input extends React.PureComponent {
  static propTypes = {
    type: PropTypes.string,
    pattern: PropTypes.string,
    inputMode: PropTypes.string,
    inputRef: PropTypes.oneOfType([
      PropTypes.func,
      PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
    ]),
    value: PropTypes.string,
    tabIndex: PropTypes.string,
    label: PropTypes.string.isRequired,
    placeholder: PropTypes.string,
    valid: PropTypes.bool,
    disabled: PropTypes.bool,
    message: PropTypes.string,
    maxLength: PropTypes.number,
    autoComplete: PropTypes.bool,
    iconPath: PropTypes.string,
    customWidth: PropTypes.number,
    onChange: PropTypes.func,
    onBlur: PropTypes.func,
    onFocus: PropTypes.func,
    theme: PropTypes.object,
  };

  static defaultProps = {
    type: 'text',
    pattern: '',
    inputMode: '',
    inputRef: () => {},
    valid: undefined,
    tabIndex: '0',
    disabled: false,
    message: '',
    maxLength: undefined,
    autoComplete: false,
    placeholder: '',
    value: '',
    iconPath: '',
    customWidth: 100,
    theme: {},
    onBlur: () => {},
    onFocus: () => {},
    onChange: () => {},
  };

  static contextType = FormContext;

  constructor(props) {
    super(props);

    this.name = getRandomInt();
    this.id = newid();
    this.input = null;

    this.state = {
      focused: false,
      onceTouched: false,
    };

    this.onChange = this.onChange.bind(this);
    this.onBlur = this.onBlur.bind(this);
    this.onFocus = this.onFocus.bind(this);
  }

  componentDidMount() {
    if (this.context) {
      this.context.onInputAddedToForm(this.input);
    }
  }

  onChange(e) {
    const { value } = e.target;
    const { type, onChange } = this.props;

    if (type === 'number') {
      const valueWithReplacedComma = value.replace(',', '.');
      // eslint-disable-next-line no-restricted-globals
      if (value !== '' && isNaN(valueWithReplacedComma)) return; // hack for type number because input with type number changing via scroll on it - it is bad feature
      onChange(valueWithReplacedComma);
    } else {
      onChange(value);
    }
  }

  onBlur(e) {
    this.props.onBlur(e);
    this.setState({ focused: false });
  }

  onFocus(e) {
    this.props.onFocus(e);
    this.setState({ focused: true, onceTouched: true });
  }

  render() {
    const {
      disabled, value, valid, label, tabIndex, placeholder, message, maxLength, iconPath, customWidth, pattern, inputMode, inputRef, theme,
    } = this.props;
    const { focused, onceTouched } = this.state;

    const className = classnames(`ui-input ${theme.inputStyle}`, {
      disabled,
      focused,
      onceTouched,
      filled: value && value.length,
      valid: valid === true,
      invalid: valid === false,
    });

    return (
      <StyledUiInput className={className} style={{ width: `${customWidth}%` }}>
        {theme.inputStyle !== 'rounded' && <label htmlFor={this.name}>{label}</label>}
        <div className={classnames(`input-wrap ${theme.inputStyle}`)}>
          <input
            id={this.name}
            ref={useCombinedRefs(inputRef, ref => this.input = ref)}
            type="text"
            pattern={pattern}
            inputMode={inputMode}
            name={this.name}
            value={value}
            maxLength={maxLength}
            tabIndex={tabIndex}
            placeholder={theme.inputStyle === 'rounded' ? label : placeholder}
            disabled={disabled}
            onChange={this.onChange}
            onBlur={this.onBlur}
            onFocus={this.onFocus}
            onKeyDown={this.props.onKeyDown}
            autoComplete={this.name}
          />
          {iconPath && (
          <div className="input-icon">
            <Svg path={iconPath} className="input-icon-svg" />
          </div>
          )}
        </div>
        <AnimatedErrorMessage in={valid === false}><span dangerouslySetInnerHTML={{ __html: message }} /></AnimatedErrorMessage>
      </StyledUiInput>
    );
  }
}

export default withTheme(Input);
